'use client';

import React, { useState, type ChangeEvent } from 'react';
import { motion, Variants } from 'framer-motion';
import { 
  Button,
  Card, 
  CardContent,
  Input,
  FormField,
} from '../../../components/ui/design-system';
import { AuthService } from '@repo/shared';
import Link from 'next/link';

// Animation variants
const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

const slideUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const passwordRequirements: PasswordRequirement[] = [
  {
    label: 'At least 8 characters long',
    test: (password) => password.length >= 8,
  },
  {
    label: 'Contains at least one uppercase letter',
    test: (password) => /[A-Z]/.test(password),
  },
  {
    label: 'Contains at least one lowercase letter',
    test: (password) => /[a-z]/.test(password),
  },
  {
    label: 'Contains at least one number',
    test: (password) => /\d/.test(password),
  },
];

function PasswordRequirements({ password }: { password: string }) {
  return (
    <div className="mt-2 space-y-2">
      <p className="text-sm text-gray-600 dark:text-gray-400">Password requirements:</p>
      <ul className="space-y-1">
        {passwordRequirements.map(({ label, test }) => {
          const isMet = test(password);
          return (
            <li key={label} className="flex items-center text-sm">
              {isMet ? (
                <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-4 w-4 text-gray-300 dark:text-gray-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              <span className={isMet ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}>
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function SignUpPage(): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);
  const handleConfirmPasswordChange = (e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value);

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password requirements
    const failedRequirements = passwordRequirements.filter(({ test }) => !test(password));
    if (failedRequirements.length > 0) {
      setError('Please meet all password requirements');
      setLoading(false);
      return;
    }

    try {
      const result = await AuthService.signUp(email, password);
      if (result.error) {
        throw new Error(result.error);
      }
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await AuthService.signInWithProvider('google');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up with Google');
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-md mx-auto px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="text-center mb-8"
        >
          <div className="mx-auto flex items-center justify-center w-12 h-12 premium-premium-radius-base-full bg-green-100 dark:bg-green-900/30">
            <svg
              className="h-6 w-6 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mt-4 mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-premium-700 dark:from-primary-400 dark:to-premium-500">
            Check your email
          </h1>
          <div className="w-16 h-1 mx-auto my-4 bg-gradient-to-r from-primary-500/50 to-premium-700/50 premium-premium-radius-base-full"></div>
          <p className="text-gray-600 dark:text-gray-300">
            We&apos;ve sent a verification link to <strong>{email}</strong>. Please check your email to verify your account.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={slideUp}
          className="mt-8 space-y-4 text-center"
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Didn&apos;t receive the email? Check your spam folder or try signing up again.
          </p>
          <div className="space-x-4">
            <Link
              href="/auth/signup"
              className="text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
            >
              Try again
            </Link>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <Link
              href="/auth/login"
              className="text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
            >
              Sign in
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-purple-500 dark:from-red-400 dark:to-purple-400">
          Create Account
        </h1>
        <div className="w-16 h-1 mx-auto my-4 bg-gradient-to-r from-red-500/50 to-purple-500/50 premium-premium-radius-base-full"></div>
        <p className="text-gray-600 dark:text-gray-300">
          Sign up to get started
        </p>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={slideUp}
      >
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-100 dark:border-gray-700/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
          <CardContent className="p-6 space-y-6">
            <form onSubmit={handleSignUp} className="space-y-6">
              <FormField label="Email" required>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Email address"
                  disabled={loading}
                  className="shadow-sm"
                />
              </FormField>

              <FormField label="Password" required>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Password"
                  disabled={loading}
                  className="shadow-sm"
                />
                <PasswordRequirements password={password} />
              </FormField>

              <FormField label="Confirm Password" required>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Confirm Password"
                  disabled={loading}
                  className="shadow-sm"
                />
              </FormField>

              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 premium-premium-radius-base-md">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                variant="primary"
                className="w-full shadow-md hover:shadow-lg"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              onClick={handleGoogleSignUp}
              variant="secondary"
              className="w-full shadow-sm hover:shadow-md"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
                />
              </svg>
              Continue with Google
            </Button>

            <div className="text-center space-y-2">
              <p className="text-sm">
                Already have an account?{' '}
                <Link href="/auth/login" className="font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                  Sign in
                </Link>
              </p>
            </div>

            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              By signing up, you agree to our{' '}
              <Link href="/terms" className="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="/privacy" className="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                Privacy Policy
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
} 